/**
 * Utility functions for formatting data
 */

/**
 * Format a number as Brazilian Real currency
 */
export const formatCurrency = (value: number): string => {
  return value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
};

/**
 * Format a date string or Date object to Brazilian format
 */
export const formatDate = (
  date: string | Date,
  options?: Intl.DateTimeFormatOptions
): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  const defaultOptions: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  };

  return dateObj.toLocaleDateString('pt-BR', options || defaultOptions);
};

/**
 * Format date with time
 */
export const formatDateTime = (date: string | Date): string => {
  return formatDate(date, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Format fuel type enum to readable string
 */
export const formatFuelType = (type: string): string => {
  const fuelTypes: Record<string, string> = {
    GASOLINA_COMUM: 'Gasolina Comum',
    GASOLINA_ADITIVADA: 'Gasolina Aditivada',
    GASOLINA_PREMIUM: 'Gasolina Premium',
    ETANOL: 'Etanol',
    DIESEL: 'Diesel',
    DIESEL_S10: 'Diesel S10',
    GNV: 'GNV',
  };
  
  return fuelTypes[type] || type;
};

/**
 * Format vehicle plate (ABC-1234 or ABC1D23)
 */
export const formatPlaca = (placa: string): string => {
  if (!placa) return '';
  
  const cleaned = placa.replace(/[^A-Z0-9]/gi, '').toUpperCase();
  
  // Old format: ABC-1234
  if (cleaned.length === 7 && /^[A-Z]{3}[0-9]{4}$/.test(cleaned)) {
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
  }
  
  // Mercosul format: ABC1D23
  if (cleaned.length === 7 && /^[A-Z]{3}[0-9][A-Z][0-9]{2}$/.test(cleaned)) {
    return cleaned;
  }
  
  return placa;
};

/**
 * Format CPF (000.000.000-00)
 */
export const formatCPF = (cpf: string): string => {
  if (!cpf) return '';
  
  const cleaned = cpf.replace(/\D/g, '');
  
  if (cleaned.length !== 11) return cpf;
  
  return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};

/**
 * Format CNPJ (00.000.000/0000-00)
 */
export const formatCNPJ = (cnpj: string): string => {
  if (!cnpj) return '';
  
  const cleaned = cnpj.replace(/\D/g, '');
  
  if (cleaned.length !== 14) return cnpj;
  
  return cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
};

/**
 * Format CPF or CNPJ automatically
 */
export const formatCPFCNPJ = (value: string): string => {
  if (!value) return '';
  
  const cleaned = value.replace(/\D/g, '');
  
  if (cleaned.length === 11) {
    return formatCPF(cleaned);
  } else if (cleaned.length === 14) {
    return formatCNPJ(cleaned);
  }
  
  return value;
};

/**
 * Format phone number ((00) 00000-0000 or (00) 0000-0000)
 */
export const formatPhone = (phone: string): string => {
  if (!phone) return '';
  
  const cleaned = phone.replace(/\D/g, '');
  
  // Mobile: (00) 00000-0000
  if (cleaned.length === 11) {
    return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  }
  
  // Landline: (00) 0000-0000
  if (cleaned.length === 10) {
    return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  }
  
  return phone;
};

/**
 * Format number with thousands separator
 */
export const formatNumber = (value: number, decimals: number = 0): string => {
  return value.toLocaleString('pt-BR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

/**
 * Format liters (0.00 L)
 */
export const formatLiters = (liters: number): string => {
  return `${formatNumber(liters, 2)} L`;
};

/**
 * Format kilometers (0 km or 0.00 km)
 */
export const formatKilometers = (km: number, decimals: number = 0): string => {
  return `${formatNumber(km, decimals)} km`;
};

/**
 * Format user role to readable string
 */
export const formatUserRole = (role: string): string => {
  const roles: Record<string, string> = {
    ADMIN: 'Administrador',
    PARCEIRO: 'Parceiro',
    PROPRIETARIO: 'Proprietário',
    MOTORISTA: 'Motorista',
    USUARIO: 'Usuário',
  };
  
  return roles[role] || role;
};

/**
 * Format relative time (há 5 minutos, há 2 horas, etc.)
 */
export const formatRelativeTime = (date: string | Date): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffMs = now.getTime() - dateObj.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return 'agora há pouco';
  if (diffMin < 60) return `há ${diffMin} ${diffMin === 1 ? 'minuto' : 'minutos'}`;
  if (diffHour < 24) return `há ${diffHour} ${diffHour === 1 ? 'hora' : 'horas'}`;
  if (diffDay < 7) return `há ${diffDay} ${diffDay === 1 ? 'dia' : 'dias'}`;
  
  return formatDate(dateObj);
};
